import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, RefreshCcw, Mail, MapPin, ChevronLeft, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e?.response?.data?.detail || "Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  useEffect(() => {
    load();
    document.title = "Leads · Peak Plumbing";
  }, []);

  return (
    <main data-testid="admin-leads-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
            <ChevronLeft size={16} /> Back to site
          </Link>
          <h1 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">
            Lead inbox
          </h1>
          <div className="flex items-center gap-2">
            {admin && typeof admin === "object" && (
              <span
                data-testid="admin-user-email"
                className="hidden sm:inline text-xs text-slate-500 mr-2"
              >
                {admin.email}
              </span>
            )}
            <button
              data-testid="admin-leads-refresh"
              onClick={load}
              className="btn-outline-dark inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              data-testid="admin-leads-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        {loading && <p className="text-slate-500">Loading…</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && leads.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center">
            <p className="text-slate-500">No leads yet. Submitted quote forms will appear here.</p>
          </div>
        )}

        <div className="grid gap-4">
          {leads.map((lead) => (
            <article
              key={lead.id}
              data-testid={`admin-lead-${lead.id}`}
              className="bg-white border border-slate-200 rounded-md p-5 md:p-6 grid md:grid-cols-12 gap-4"
            >
              <div className="md:col-span-4">
                <div className="font-display text-lg font-bold text-slate-900">{lead.name}</div>
                <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-orange-600 font-semibold mt-1 text-sm">
                  <Phone size={14} /> {lead.phone}
                </a>
                {lead.email && (
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-slate-600 mt-0.5 text-sm">
                    <Mail size={13} /> {lead.email}
                  </a>
                )}
                {lead.address && (
                  <div className="flex items-center gap-2 text-slate-500 mt-0.5 text-sm">
                    <MapPin size={13} /> {lead.address}
                  </div>
                )}
              </div>
              <div className="md:col-span-6">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Service requested</div>
                <div className="text-slate-900 font-semibold">{lead.service || "—"}</div>
                {lead.message && (
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed">{lead.message}</p>
                )}
              </div>
              <div className="md:col-span-2 text-right md:text-left">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Submitted</div>
                <div className="text-slate-900 font-mono text-xs">
                  {new Date(lead.created_at).toLocaleString()}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 md:justify-start justify-end">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700">
                    {lead.status}
                  </span>
                  {lead.email_sent ? (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">
                      emailed
                    </span>
                  ) : (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700">
                      no-email
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </main>
  );
}
